import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevealOnView } from '@/components/ui';
import {
  isLensProduct,
  type Product,
  type ProductSection as ProductSectionData,
  type SolutionProduct,
} from '@/pages/Products/content';
import { CONTENT_SLIDE, HEADER_FADE, REVEAL_TIMING, useInView } from '@/utils/animation';
import { ProductRow } from './ProductRow';
import { SectionHeader } from './SectionHeader';
import { useRegion } from '@/utils/useRegion';

interface ProductSectionProps {
  section: ProductSectionData;
  /** When true, paints the section background with the same tint as the hero (#E2DFDA at 20%). */
  tinted?: boolean;
  /**
   * When true, the reveal cascade fires on mount instead of waiting for the
   * section to scroll into view. Used for the first section on the page
   * (Clear family) which is already in view at page load.
   */
  revealOnMount?: boolean;
  /**
   * When true, the very first product image in this section is eager-loaded as
   * the page's LCP candidate. Set only on the first section on the page.
   */
  prioritizeFirstImage?: boolean;
}

/**
 * A product family section (Clear / Standard / Color).
 *
 * Figma (large, 1440px):
 *   - Section vertical padding: 200px top + 200px bottom
 *   - Header: 2-col, 440 title + 160 gap + 600 intro inside 1200 content
 *   - Header → 120px gap → full-width 1px hairline divider
 *   - Divider → 121px gap → first product row
 *   - Row → 120px gap → divider → 121px gap → next row (dividers between EVERY row)
 *
 * Responsive:
 *   Mobile        : 1-col stack header (gap 40); section padding py-30 (120) and px-6 (24)
 *   Tablet        : 2-col header w-60 + flex-1; py-30, px-14 (56)
 *   Small         : 2-col header w-60 + flex-1; py-50 (200), px-20 (80)
 *   XL+           : 2-col header w-[440px] + w-[600px] justify-between, 1200 max content
 *                   py-50 (200), px-30 (120)
 *
 * `scroll-mt-[160px]` offsets the fixed NavigationPill when scrolling to anchor links.
 *
 * Dividers (full-width 1px hairlines) appear only between groups, not between
 * consecutive products inside the same group. This separates spherical/toric or
 * standard/phantom subgroups while keeping rows within a subgroup visually unified.
 */
// Reveal cascade — descriptor starts 25% into the header fade (see
// SectionHeader); rows then start 30% into the descriptor's slide.
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.25);
const ROWS_DELAY_MS = INTRO_DELAY_MS + Math.round(REVEAL_TIMING.durationMs * 0.3);

export function ProductSection({
  section,
  tinted = false,
  revealOnMount = false,
  prioritizeFirstImage = false,
}: ProductSectionProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { threshold: 0 });
  const [mountedVisible, setMountedVisible] = useState(false);
  const [inViewVisible, setInViewVisible] = useState(false);
  // 100ms beat after page-open before the cascade kicks off — gives the page
  // a moment to settle visually before the reveal sequence begins.
  useEffect(() => {
    if (!revealOnMount) return;
    const t = setTimeout(() => setMountedVisible(true), 100);
    return () => clearTimeout(t);
  }, [revealOnMount]);
  // 100ms beat after the section first enters view before the cascade fires —
  // matches the page-open beat so every section feels consistent.
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setInViewVisible(true), 100);
    return () => clearTimeout(t);
  }, [inView]);
  const visible = revealOnMount ? mountedVisible : inViewVisible;

  const country = useRegion();
  const isAvailable = (product: Product | SolutionProduct) => {
    if (!isLensProduct(product)) return true; // solution products handled separately
    if (!country) return true; // don't hide anything before geo resolves
    if (product.unavailableIn?.includes(country)) return false;
    if (product.availability && !product.availability.includes(country)) return false;

    return true;
  };

  return (
    <section
      ref={sectionRef}
      id={section.id}
      aria-labelledby={`${section.id}-heading`}
      className={`scroll-mt-[160px] px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30 ${
        tinted ? 'bg-clearlab-divider-light/20' : ''
      }`}
    >
      <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
        <SectionHeader
          id={`${section.id}-heading`}
          title={t(`products.sections.${section.sectionKey}.title`)}
          intro={t(`products.sections.${section.sectionKey}.intro`)}
          layout="fluid"
          visible={visible}
        />

        <RevealOnView
          {...CONTENT_SLIDE}
          inView={visible}
          delayMs={ROWS_DELAY_MS}
          className="flex flex-col gap-30"
        >
          <hr className="w-full border-0 border-t border-clearlab-divider-darker/60" />

          <div className="flex flex-col gap-30">
            {section.groups
              .map((group) => ({
                ...group,
                products: group.products.filter(isAvailable),
              }))
              .filter((group) => group.products.length > 0)
              .map((group, groupIdx) => (
                <Fragment key={groupIdx}>
                  {groupIdx > 0 ? (
                    <hr className="w-full border-0 border-t border-clearlab-divider-darker/60" />
                  ) : null}
                  {group.products.map((product, productIdx) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      specsLabel={t('products.labels.lensSpecifications')}
                      inColorFamily={section.id === 'color-family'}
                      priority={prioritizeFirstImage && groupIdx === 0 && productIdx === 0}
                    />
                  ))}
                </Fragment>
              ))}
          </div>
        </RevealOnView>
      </div>
    </section>
  );
}
